/**
 * Command registry. The palette (mod+k) walks the registered list and lets the
 * user run a command by fuzzy-matching its title. Commands are mounted via
 * React context so any descendant component can register.
 *
 * Stage 1 ships the bare registry + a few dev-mode defaults ("Go to
 * workspace", "Open keyboard shortcuts"). Stage 2+ adds navigation actions.
 */
import { create } from "zustand";

export interface Command {
  id: string;
  title: string;
  description?: string;
  group?: string;
  keywords?: string[];
  shortcut?: string[]; // e.g. ["mod", "shift", "p"]
  perform: () => void | Promise<void>;
}

interface CommandStore {
  commands: Map<string, Command>;
  paletteOpen: boolean;
  shortcutsOpen: boolean;
  register: (cmd: Command) => () => void;
  openPalette: () => void;
  closePalette: () => void;
  togglePalette: () => void;
  openShortcuts: () => void;
  closeShortcuts: () => void;
  toggleShortcuts: () => void;
}

export const useCommandStore = create<CommandStore>((set, get) => ({
  commands: new Map(),
  paletteOpen: false,
  shortcutsOpen: false,
  register: (cmd) => {
    set((s) => {
      const next = new Map(s.commands);
      next.set(cmd.id, cmd);
      return { commands: next };
    });
    return () => {
      set((s) => {
        const next = new Map(s.commands);
        next.delete(cmd.id);
        return { commands: next };
      });
    };
  },
  openPalette: () => set({ paletteOpen: true }),
  closePalette: () => set({ paletteOpen: false }),
  togglePalette: () => set((s) => ({ paletteOpen: !s.paletteOpen })),
  openShortcuts: () => set({ shortcutsOpen: true }),
  closeShortcuts: () => set({ shortcutsOpen: false }),
  toggleShortcuts: () => set((s) => ({ shortcutsOpen: !s.shortcutsOpen })),
}));

/** Register a command for the lifetime of the calling component. */
export function useRegisterCommand(cmd: Command): void {
  const register = useCommandStore((s) => s.register);
  // biome-ignore lint/correctness/useExhaustiveDependencies: cmd identity is the change signal
  useEffect(() => register(cmd), [register, cmd.id, cmd.title, cmd.perform]);
}

// We import useEffect lazily so the module is dependency-free for SSR/test.
import { useEffect } from "react";
